/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import {
  HEADER_RETRY_AFTER,
  INITIAL_BACKOFF_MS,
  MAX_BACKOFF_MS,
  MAX_JITTER_MS,
  RETRYABLE_STATUS_CODES,
} from './constants.js';

/** Default sleep using `setTimeout`. Overridable via `EcsperClientConfig.sleep`. */
export function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Random jitter in the inclusive range `[0, MAX_JITTER_MS]`. */
function defaultJitter(): number {
  return Math.floor(Math.random() * (MAX_JITTER_MS + 1));
}

/**
 * Compute the exponential backoff window for a given attempt index (zero-based),
 * capped at `MAX_BACKOFF_MS` and offset by random jitter in `[0, MAX_JITTER_MS]`.
 *
 * Attempt 0 ≈ 500ms, attempt 1 ≈ 1000ms, attempt 2 ≈ 2000ms, ... up to 10s.
 */
export function computeBackoff(attempt: number, jitter: () => number = defaultJitter): number {
  const exponential = INITIAL_BACKOFF_MS * 2 ** attempt;
  const capped = Math.min(exponential, MAX_BACKOFF_MS);
  return capped + jitter();
}

/**
 * Parse a `Retry-After` header value into a sleep duration in milliseconds.
 *
 * Per RFC 7231 §7.1.3 the value is either delta-seconds or an HTTP-date. When
 * both forms are unparseable or the value is absent, returns `null`.
 */
export function parseRetryAfter(
  headerValue: string | null,
  now: () => number = Date.now,
): number | null {
  if (headerValue === null || headerValue.trim().length === 0) return null;
  const trimmed = headerValue.trim();
  const seconds = Number(trimmed);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return Math.round(seconds * 1000);
  }
  const dateMs = Date.parse(trimmed);
  if (Number.isFinite(dateMs)) {
    const delta = dateMs - now();
    return delta > 0 ? delta : 0;
  }
  return null;
}

/**
 * Determine whether a given HTTP status code is retryable by the client.
 */
export function isRetryableStatus(status: number): boolean {
  return RETRYABLE_STATUS_CODES.includes(status);
}

/**
 * Compute the delay before the next attempt given the response (if any) and
 * the attempt number that just failed (zero-based). For 429 responses with a
 * `Retry-After` header, that value wins; otherwise exponential backoff is
 * used.
 */
export function nextDelayMs(args: {
  readonly response: Response | null;
  readonly attempt: number;
  readonly jitter?: () => number;
  readonly now?: () => number;
}): number {
  const { response, attempt, jitter, now } = args;
  if (response !== null && response.status === 429) {
    const headerValue = response.headers.get(HEADER_RETRY_AFTER);
    const fromHeader = parseRetryAfter(headerValue, now);
    if (fromHeader !== null) return fromHeader;
  }
  return computeBackoff(attempt, jitter);
}
